import { useState, useMemo } from "react";
import {
  CornerUpLeftIcon,
  FileTextIcon,
  CheckCircle2,
  XCircle,
  ChevronRight,
  ChevronDown,
  Activity,
  Clock,
  Filter,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useGetSingleSuiteResult } from "../../../useQuery";
import { useNotification } from "../../../hooks";
import jsPDF from "jspdf";

const isPass = (r) => r?.startsWith("PASS");

const tryParseJSON = (str) => {
  if (typeof str !== "string") return typeof str === "object" && str !== null ? str : null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

const generateCleanPDF = (suiteResult, testCaseResults, notify) => {
  try {
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

    const PW = 595,
      PH = 842;
    const ML = 40,
      MR = 40,
      MT = 30,
      MB = 40;
    const CW = PW - ML - MR;

    const C = {
      brand: [37, 99, 235],
      brandDark: [29, 78, 216],
      dark: [17, 24, 39],
      mid: [75, 85, 99],
      light: [156, 163, 175],
      hairline: [229, 231, 235],
      pageBg: [248, 250, 252],
      white: [255, 255, 255],
      green: [5, 150, 105],
      greenBg: [236, 253, 245],
      greenBdr: [167, 243, 208],
      red: [185, 28, 28],
      redBg: [254, 242, 242],
      redBdr: [252, 165, 165],
      codeBg: [30, 30, 30],
      codeText: [186, 230, 253],
    };

    const fill = (rgb) => pdf.setFillColor(...rgb);
    const draw = (rgb) => pdf.setDrawColor(...rgb);
    const color = (rgb) => pdf.setTextColor(...rgb);
    const lw = (w) => pdf.setLineWidth(w);

    const rect = (x, y, w, h, style = "F") => pdf.rect(x, y, w, h, style);
    const rrect = (x, y, w, h, r, style = "F") => {
      try {
        pdf.roundedRect(x, y, w, h, r, r, style);
      } catch {
        pdf.rect(x, y, w, h, style);
      }
    };

    let y = MT;

    const addPage = () => {
      pdf.addPage();
      fill(C.pageBg);
      rect(0, 0, PW, PH);
      drawPageHeader();
      y = 80;
    };

    const ensureSpace = (need) => {
      if (y + need > PH - MB) addPage();
    };

    const safeText = (str) =>
      String(str ?? "")
        .replace(/[\u2019\u2018]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/\u2013/g, "-")
        .replace(/\u2014/g, "--")
        .replace(/[^\x20-\x7E\t\n\r]/g, "");

    const txt = (str, x, ty, opts = {}) => {
      try {
        const text = safeText(str);
        if (opts.align) {
          pdf.text(text, x, ty, opts);
        } else {
          pdf.text(text, x, ty);
        }
      } catch (e) {
        console.warn("Text rendering skipped:", str);
      }
    };

    const hline = (ty, col = C.hairline, weight = 0.5) => {
      draw(col);
      lw(weight);
      pdf.line(ML, ty, PW - MR, ty);
    };

    const drawPageHeader = () => {
      fill(C.brand);
      rect(0, 0, PW, 40);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      color(C.white);
      txt("NOGRUNT  |  API Test Report", ML, 25);
      txt(safeText(suiteResult.testSuiteName), PW - MR, 25);
    };

    fill(C.brand);
    rect(0, 0, PW, 50);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    color(C.white);
    txt("NOGRUNT  |  API Test Report", ML, 32);
    const dateStr = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    txt(dateStr, PW - MR, 32);

    y = 72;
    fill(C.pageBg);
    rect(0, 50, PW, PH - 50);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(24);
    color(C.dark);
    txt(suiteResult.testSuiteName, ML, y);
    y += 18;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    color(C.light);
    txt("Results Summary", ML, y);
    y += 20;

    hline(y);
    y += 18;

    const executedAt = new Date(suiteResult.executedDate);
    const stats = [
      {
        label: "EXECUTED",
        value: executedAt.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        sub: executedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
        col: C.dark,
      },
      { label: "DURATION", value: `${suiteResult.duration}s`, col: C.dark },
      { label: "PASSED", value: String(suiteResult.passCount), sub: "test cases", col: C.green },
      {
        label: "FAILED",
        value: String(suiteResult.failCount),
        sub: "test cases",
        col: suiteResult.failCount > 0 ? C.red : C.light,
      },
    ];

    const cardW = (CW - 12) / 4;
    const cardH = 60;
    stats.forEach((s, i) => {
      const cx = ML + i * (cardW + 4);
      fill(C.white);
      draw(C.hairline);
      lw(0.5);
      rrect(cx, y, cardW, cardH, 4, "FD");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7);
      color(C.light);
      txt(s.label, cx + 10, y + 16);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      color(s.col);
      txt(s.value, cx + 10, y + 36);

      if (s.sub) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        color(C.light);
        txt(s.sub, cx + 10, y + 50);
      }
    });
    y += cardH + 18;

    const allR = (testCaseResults || []).flatMap((tc) => Object.values(tc.testResults || {}));
    const totalPass = allR.filter(isPass).length;
    const totalFail = allR.length - totalPass;
    const passRate = allR.length ? Math.round((totalPass / allR.length) * 100) : 0;

    if (allR.length) {
      const ovH = 68;
      fill(C.white);
      draw(C.hairline);
      lw(0.5);
      rrect(ML, y, CW, ovH, 4, "FD");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      color(C.dark);
      txt("Assertion Overview", ML + 12, y + 18);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      color(C.light);
      txt(`${allR.length} assertions across ${testCaseResults.length} test cases`, ML + 12, y + 30);

      const passLabel = `${totalPass} passed`;
      const failLabel = `${totalFail} failed`;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      color(C.green);
      txt(passLabel, PW - MR - 12, y + 18);

      if (totalFail > 0) {
        const passW = pdf.getTextWidth(passLabel);
        color(C.red);
        txt(failLabel, PW - MR - 12 - passW - 10, y + 18);
      }

      const barX = ML + 12,
        barY = y + 42,
        barW = CW - 24,
        barH = 8;
      fill(C.hairline);
      rrect(barX, barY, barW, barH, 2);
      if (totalPass > 0) {
        fill(C.green);
        rrect(barX, barY, barW * (totalPass / allR.length), barH, 2);
      }
      if (totalFail > 0) {
        const fx = barX + barW * (totalPass / allR.length);
        fill(C.red);
        rrect(fx, barY, barW * (totalFail / allR.length), barH, 2);
      }

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      color(C.mid);
      txt(`${passRate}% pass rate`, PW / 2, y + 62);

      y += ovH + 20;
    }

    ensureSpace(30);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    color(C.dark);
    txt(`Test Cases (${testCaseResults?.length || 0})`, ML, y);
    y += 16;

    (testCaseResults || []).forEach((tc) => {
      const results = Object.entries(tc.testResults || {});
      const tcPass = results.filter(([, r]) => isPass(r)).length;
      const tcFail = results.length - tcPass;
      const allPassed = tcFail === 0;

      const headerH = 48;
      const assertionRowH = 28;
      const hasResponse = !!tc.originalResponse?.message;
      const responseH = hasResponse ? 36 : 0;
      const cardH = headerH + results.length * assertionRowH + responseH + 16;

      ensureSpace(cardH + 10);

      const cx = ML;

      fill(allPassed ? C.green : C.red);
      rect(cx, y, 4, cardH);

      fill(C.white);
      draw(C.hairline);
      lw(0.5);
      rect(cx + 4, y, CW - 4, cardH, "FD");

      const tcName = tc.testCaseName || "Unnamed Test Case";
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      color(C.dark);
      txt(tcName, cx + 16, y + 18);

      const metaParts = [];
      if (tc.originalResponse?.status) metaParts.push(`HTTP ${tc.originalResponse.status}`);
      metaParts.push(`${results.length} assertion${results.length !== 1 ? "s" : ""}`);
      if (tcPass > 0) metaParts.push(`${tcPass} passed`);
      if (tcFail > 0) metaParts.push(`${tcFail} failed`);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      color(C.light);
      txt(metaParts.join("   |   "), cx + 16, y + 32);

      const pillW = 72,
        pillH = 18;
      const pillX = PW - MR - pillW;
      const pillY = y + 14;
      const pillColor = allPassed ? C.greenBg : C.redBg;
      const pillBdr = allPassed ? C.greenBdr : C.redBdr;
      const pillText = allPassed ? "All Passed" : `${tcFail} Failed`;
      const pillTxt = allPassed ? C.green : C.red;

      fill(pillColor);
      draw(pillBdr);
      lw(0.5);
      rrect(pillX, pillY, pillW, pillH, 3, "FD");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      color(pillTxt);
      txt(pillText, pillX + pillW / 2, pillY + 11.5);

      draw(C.hairline);
      lw(0.4);
      pdf.line(cx + 12, y + headerH - 4, PW - MR - 4, y + headerH - 4);

      let ay = y + headerH + 4;
      results.forEach(([name, result]) => {
        const pass = isPass(result);
        const msg = result.replace(/^(PASS|FAIL)\s*/i, "").trim();
        const rowH = 24;

        fill(pass ? C.greenBg : C.redBg);
        draw(pass ? C.greenBdr : C.redBdr);
        lw(0.3);
        rrect(cx + 12, ay, CW - 16, rowH, 2, "FD");

        const labelW = 28,
          labelH = 12;
        fill(pass ? C.green : C.red);
        rrect(cx + 16, ay + 6, labelW, labelH, 2);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(6.5);
        color(C.white);
        txt(pass ? "PASS" : "FAIL", cx + 16 + labelW / 2, ay + 14);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        color(C.dark);
        txt(name, cx + 50, ay + 10);

        if (msg) {
          const shortMsg = msg.length > 90 ? msg.slice(0, 90) + "..." : msg;
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(7);
          color(pass ? C.green : C.red);
          txt(shortMsg, cx + 50, ay + 19);
        }

        ay += rowH + 3;
      });

      if (hasResponse) {
        const rStatus = tc.originalResponse.status;
        const rMsg = tc.originalResponse.message;
        const msgStr = typeof rMsg === "object" ? JSON.stringify(rMsg) : String(rMsg || "");
        const isOk = rStatus >= 200 && rStatus < 300;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7);
        color(C.light);
        txt("RESPONSE DETAILS", cx + 16, ay + 10);

        const badgeW = 26,
          badgeH = 12;
        fill(isOk ? C.greenBg : C.redBg);
        draw(isOk ? C.greenBdr : C.redBdr);
        lw(0.3);
        rrect(cx + 106, ay + 3, badgeW, badgeH, 2, "FD");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7);
        color(isOk ? C.green : C.red);
        txt(String(rStatus), cx + 106 + badgeW / 2, ay + 11.5);

        const codeH = 22;
        fill(C.codeBg);
        rrect(cx + 12, ay + 16, CW - 16, codeH, 2);
        pdf.setFont("courier", "normal");
        pdf.setFontSize(6.5);
        color(C.codeText);
        const snippet = msgStr.length > 200 ? msgStr.slice(0, 200) + "..." : msgStr;
        const codeLines = pdf.splitTextToSize(snippet, CW - 28);
        codeLines.slice(0, 2).forEach((ln, li) => txt(ln, cx + 18, ay + 26 + li * 8));

        ay += 16 + codeH + 4;
      }

      y = ay + 14;
    });

    const totalPages = pdf.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      draw(C.hairline);
      lw(0.5);
      pdf.line(ML, PH - MB + 8, PW - MR, PH - MB + 8);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      color(C.light);
      txt("Generated by Nogrunt  |  API Test Automation", ML, PH - MB + 20);
      txt(`Page ${p} of ${totalPages}`, PW - MR, PH - MB + 20);
    }

    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    pdf.save(`${safeText(suiteResult.testSuiteName)}_TestReport_${ts}.pdf`);
    notify("PDF report generated successfully!", "success");
  } catch (err) {
    console.error("PDF generation error:", err);
    notify(`Failed to generate PDF: ${err.message}`, "error");
  }
};

const JsonNode = ({ data, depth = 0 }) => {
  const [collapsed, setCollapsed] = useState(depth > 1);
  const pad = depth * 14;

  if (data === null) return <span className="text-rose-400">null</span>;
  if (typeof data === "boolean") return <span className="text-amber-400">{String(data)}</span>;
  if (typeof data === "number") return <span className="text-sky-400">{data}</span>;
  if (typeof data === "string") return <span className="text-emerald-400">"{data}"</span>;

  if (Array.isArray(data)) {
    if (!data.length) return <span className="text-gray-500">[]</span>;
    return (
      <span>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="cursor-pointer hover:opacity-70 select-none"
        >
          {collapsed ? (
            <span className="text-blue-400 font-mono text-xs bg-blue-950/40 px-1 rounded">
              [{data.length} items]
            </span>
          ) : (
            <span className="text-gray-500 font-mono text-xs">[</span>
          )}
        </button>
        {!collapsed && (
          <>
            {data.map((v, i) => (
              <div
                key={i}
                style={{ paddingLeft: pad + 14 }}
                className="leading-relaxed font-mono text-xs"
              >
                <JsonNode data={v} depth={depth + 1} />
                {i < data.length - 1 && <span className="text-gray-600">,</span>}
              </div>
            ))}
            <div style={{ paddingLeft: pad }} className="font-mono text-xs text-gray-500">
              ]
            </div>
          </>
        )}
      </span>
    );
  }

  if (typeof data === "object") {
    const keys = Object.keys(data);
    if (!keys.length) return <span className="text-gray-500">{"{}"}</span>;
    return (
      <span>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="cursor-pointer hover:opacity-70 select-none"
        >
          {collapsed ? (
            <span className="text-blue-400 font-mono text-xs bg-blue-950/40 px-1 rounded">
              {"{"}
              {keys.length} {keys.length === 1 ? "key" : "keys"}
              {"}"}
            </span>
          ) : (
            <span className="text-gray-500 font-mono text-xs">{"{"}</span>
          )}
        </button>
        {!collapsed && (
          <>
            {keys.map((k, i) => (
              <div
                key={k}
                style={{ paddingLeft: pad + 14 }}
                className="leading-relaxed font-mono text-xs"
              >
                <span className="text-violet-400">"{k}"</span>
                <span className="text-gray-500">: </span>
                <JsonNode data={data[k]} depth={depth + 1} />
                {i < keys.length - 1 && <span className="text-gray-600">,</span>}
              </div>
            ))}
            <div style={{ paddingLeft: pad }} className="font-mono text-xs text-gray-500">
              {"}"}
            </div>
          </>
        )}
      </span>
    );
  }
  return <span className="text-gray-300 font-mono text-xs">{String(data)}</span>;
};

const JsonViewer = ({ raw }) => {
  const parsed = tryParseJSON(raw);
  const [copied, setCopied] = useState(false);
  const text = parsed ? JSON.stringify(parsed, null, 2) : String(raw ?? "");

  return (
    <div className="rounded-b-xl overflow-hidden bg-[#0d1117]">
      <div className="flex items-center justify-between px-4 py-2 bg-[#161b22] border-b border-gray-800">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${parsed ? "bg-emerald-500" : "bg-amber-400"}`} />
          <span className="font-mono text-gray-400 text-[11px]">
            {parsed ? "application/json" : "text/plain"}
          </span>
        </div>
        <button
          onClick={() => {
            navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="text-[11px] text-gray-400 hover:text-white cursor-pointer px-2.5 py-1 rounded-md bg-gray-800 hover:bg-gray-700 transition-colors font-mono"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <div className="p-4 overflow-x-auto max-h-72 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {parsed ? (
          <div className="font-mono text-xs leading-relaxed">
            <JsonNode data={parsed} depth={0} />
          </div>
        ) : (
          <pre className="text-gray-300 font-mono text-xs whitespace-pre-wrap break-words leading-relaxed">
            {text}
          </pre>
        )}
      </div>
    </div>
  );
};

const ResponseDetails = ({ response, statusCode }) => {
  const [open, setOpen] = useState(false);
  if (!response) return null;
  const isOk = parseInt(statusCode) >= 200 && parseInt(statusCode) < 300;
  return (
    <div className="mt-3 rounded-xl overflow-hidden border border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <Activity size={12} className="text-gray-400" />
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
            Response Details
          </span>
          {statusCode && (
            <span
              className={`px-1.5 py-0.5 rounded text-[11px] font-bold ${isOk ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}
            >
              {statusCode}
            </span>
          )}
        </div>
        {open ? (
          <ChevronDown size={12} className="text-gray-400" />
        ) : (
          <ChevronRight size={12} className="text-gray-400" />
        )}
      </button>
      {open && <JsonViewer raw={response} />}
    </div>
  );
};

const Chip = ({ label, active, color = "blue", onClick, onRemove }) => {
  const palette = {
    blue: {
      on: "bg-blue-600 text-white border-blue-600",
      off: "bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600",
    },
    green: {
      on: "bg-emerald-600 text-white border-emerald-600",
      off: "bg-white text-gray-600 border-gray-200 hover:border-emerald-400 hover:text-emerald-700",
    },
    red: {
      on: "bg-red-500 text-white border-red-500",
      off: "bg-white text-gray-600 border-gray-200 hover:border-red-400 hover:text-red-600",
    },
  };
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all cursor-pointer ${active ? palette[color].on : palette[color].off}`}
    >
      {label}
      {active && onRemove && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 flex items-center hover:opacity-70"
        >
          <X size={12} />
        </span>
      )}
    </button>
  );
};

const SingleSuiteResult = ({ suiteResult, onBack }) => {
  const notify = useNotification();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedTestCaseIndex, setSelectedTestCaseIndex] = useState(null);

  const { data: testCaseResults, isLoading, error } = useGetSingleSuiteResult(suiteResult.id);

  const allResults = useMemo(
    () => (testCaseResults ?? []).flatMap((tc) => Object.values(tc.testResults || {})),
    [testCaseResults],
  );
  const passCount = allResults.filter(isPass).length;
  const failCount = allResults.length - passCount;
  const passRate = allResults.length ? Math.round((passCount / allResults.length) * 100) : 0;
  const executedAt = useMemo(() => new Date(suiteResult.executedDate), [suiteResult.executedDate]);

  const filteredCases = useMemo(() => {
    if (!testCaseResults) return [];
    return testCaseResults.filter((tc) => {
      if (!statusFilter) return true;
      const vals = Object.values(tc.testResults || {});
      return statusFilter === "pass" ? vals.every(isPass) : vals.some((r) => !isPass(r));
    });
  }, [testCaseResults, statusFilter]);

  const activeFilterCount = statusFilter ? 1 : 0;

  const clearAll = () => {
    setStatusFilter(null);
  };

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    setTimeout(() => {
      generateCleanPDF(suiteResult, filteredCases, notify);
      setIsGeneratingPDF(false);
    }, 80);
  };

  const selectedTestCase =
    selectedTestCaseIndex !== null && selectedTestCaseIndex < filteredCases.length
      ? filteredCases[selectedTestCaseIndex]
      : null;

  if (isLoading)
    return (
      <div className="font-apple bg-gray-50 h-full max-h-[100vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading results...</span>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="font-apple bg-gray-50 h-full max-h-[100vh] flex items-center justify-center">
        <div className="text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-3 rounded-lg">
          Error: {error.message}
        </div>
      </div>
    );

  return (
    <div className="font-apple bg-gray-50 h-full max-h-[100vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-5 py-3.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <CornerUpLeftIcon size={18} />
          </button>
          <div className="h-5 w-px bg-gray-200" />
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Results Summary
            </p>
            <h1 className="text-base font-bold text-gray-900 leading-tight">
              {suiteResult.testSuiteName}
            </h1>
          </div>
        </div>
        {testCaseResults?.length > 0 && (
          <button
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 rounded-lg cursor-pointer disabled:opacity-40 transition-colors"
          >
            <FileTextIcon size={14} />
            {isGeneratingPDF ? "Generating..." : "Download PDF"}
          </button>
        )}
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left Panel - Test Cases List */}
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          {/* Stats Bar */}
          <div className="p-4 border-b border-gray-200 bg-gray-50 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Executed
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {executedAt.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                  })}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Duration
                </p>
                <p className="text-sm font-semibold text-gray-900">{suiteResult.duration}s</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Pass Rate
                </p>
                <p className="text-sm font-semibold text-emerald-600">{passRate}%</p>
              </div>
            </div>

            {/* Assertion Bar */}
            {allResults.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Assertions
                </p>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-600">{allResults.length} total</span>
                  <span className="text-emerald-600">{passCount} passed</span>
                  {failCount > 0 && <span className="text-red-600">{failCount} failed</span>}
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex">
                  <div
                    className="bg-emerald-500 h-full transition-all"
                    style={{ width: `${passRate}%` }}
                  />
                  {failCount > 0 && (
                    <div className="bg-red-400 h-full" style={{ width: `${100 - passRate}%` }} />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          {testCaseResults?.length > 0 && (
            <div className="p-4 border-b border-gray-200 space-y-3">
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`w-full inline-flex items-center justify-between px-4 py-2 rounded-lg text-sm font-semibold border transition-all cursor-pointer ${
                  showFilterPanel || activeFilterCount > 0
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600"
                }`}
              >
                <div className="flex items-center gap-2">
                  <SlidersHorizontal size={14} />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="bg-white/30 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
              </button>

              {showFilterPanel && (
                <div className="space-y-2">
                  <div className="flex gap-2 flex-wrap">
                    <Chip
                      label="All results"
                      active={!statusFilter}
                      color="blue"
                      onClick={() => setStatusFilter(null)}
                    />
                    <Chip
                      label="Passed only"
                      active={statusFilter === "pass"}
                      color="green"
                      onClick={() => setStatusFilter(statusFilter === "pass" ? null : "pass")}
                    />
                    <Chip
                      label="Failed only"
                      active={statusFilter === "fail"}
                      color="red"
                      onClick={() => setStatusFilter(statusFilter === "fail" ? null : "fail")}
                    />
                  </div>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearAll}
                      className="text-xs text-gray-400 hover:text-red-500 cursor-pointer underline underline-offset-2 transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              )}

              {activeFilterCount > 0 && (
                <div className="text-xs text-gray-500">
                  {filteredCases.length} of {testCaseResults.length} shown
                </div>
              )}
            </div>
          )}

          {/* Test Cases List */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
            {filteredCases.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center p-4">
                <div>
                  <Filter size={24} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {statusFilter
                      ? `No test cases match the "${statusFilter === "pass" ? "passed" : "failed"}" filter`
                      : "No test cases available"}
                  </p>
                </div>
              </div>
            ) : (
              filteredCases.map((testCase, idx) => {
                const results = Object.entries(testCase.testResults || {});
                const casePass = results.filter(([, r]) => isPass(r)).length;
                const caseFail = results.length - casePass;
                const allPassed = caseFail === 0;
                const isSelected = selectedTestCase === testCase;

                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedTestCaseIndex(idx)}
                    className={`flex items-center px-3 py-3 hover:bg-[rgba(0,0,0,0.04)] cursor-pointer group transition-all duration-200 border-b border-[#e0e0e0] last:border-b-0 ${isSelected ? "bg-blue-50 border-l-2 border-l-blue-600" : ""}`}
                  >
                    <span
                      className={`font-semibold text-sm w-12 ${allPassed ? "text-emerald-600" : "text-red-600"}`}
                    >
                      {allPassed ? "PASS" : "FAIL"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {testCase.testCaseName || `Test Case ${idx + 1}`}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {results.length} assertion{results.length !== 1 ? "s" : ""} • {casePass}{" "}
                        passed
                        {caseFail > 0 ? ` • ${caseFail} failed` : ""}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel - Test Case Details */}
        <div className="flex-1 overflow-y-auto bg-gray-50 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          {selectedTestCase ? (
            <div className="p-6 space-y-5">
              {(() => {
                const results = Object.entries(selectedTestCase.testResults || {});
                const casePass = results.filter(([, r]) => isPass(r)).length;
                const caseFail = results.length - casePass;
                const allPassed = caseFail === 0;
                const expectedStatusCode = selectedTestCase.expectedStatusCode;

                return (
                  <>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                        Test Case
                      </p>
                      <h2 className="text-lg font-bold text-gray-900">
                        {selectedTestCase.testCaseName || `Test Case ${selectedTestCaseIndex + 1}`}
                      </h2>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white border border-gray-100 rounded-lg px-4 py-3">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                          Assertions
                        </p>
                        <p className="text-2xl font-bold text-gray-900">{results.length}</p>
                      </div>
                      <div className="bg-white border border-gray-100 rounded-lg px-4 py-3">
                        <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1">
                          Passed
                        </p>
                        <p className="text-2xl font-bold text-emerald-600">{casePass}</p>
                      </div>
                      <div
                        className={`border rounded-lg px-4 py-3 ${caseFail > 0 ? "bg-red-50 border-red-200" : "bg-white border-gray-100"}`}
                      >
                        <p
                          className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${
                            caseFail > 0 ? "text-red-600" : "text-gray-400"
                          }`}
                        >
                          Failed
                        </p>
                        <p
                          className={`text-2xl font-bold ${
                            caseFail > 0 ? "text-red-600" : "text-gray-400"
                          }`}
                        >
                          {caseFail}
                        </p>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div
                      className={`flex items-center gap-3 p-4 rounded-lg border ${
                        allPassed ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
                      }`}
                    >
                      {allPassed ? (
                        <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0" />
                      ) : (
                        <XCircle size={20} className="text-red-600 flex-shrink-0" />
                      )}
                      <div>
                        <p
                          className={`text-sm font-semibold ${
                            allPassed ? "text-emerald-900" : "text-red-900"
                          }`}
                        >
                          {allPassed
                            ? "All Assertions Passed"
                            : `${caseFail} Assertion${caseFail !== 1 ? "s" : ""} Failed`}
                        </p>
                        {selectedTestCase.expectedStatusCode && (
                          <p
                            className={`text-xs mt-0.5 ${allPassed ? "text-emerald-700" : "text-red-700"}`}
                          >
                            HTTP {selectedTestCase.expectedStatusCode}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Expected Status Code */}
                    {caseFail > 0 && expectedStatusCode && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2">
                          Expected
                        </p>
                        <p className="text-sm font-semibold text-amber-900">
                          Status Code: {expectedStatusCode}
                        </p>
                      </div>
                    )}

                    {/* Assertions */}
                    <div>
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">
                        Assertions
                      </h3>
                      <div className="space-y-1.5">
                        {results.map(([name, result]) => {
                          const pass = isPass(result);
                          const message = result.replace(/^(PASS|FAIL)\s*/i, "").trim();
                          return (
                            <div
                              key={name}
                              className={`flex items-start gap-3 px-3.5 py-2.5 rounded-lg border ${
                                pass
                                  ? "bg-emerald-50/60 border-emerald-100"
                                  : "bg-red-50/60 border-red-100"
                              }`}
                            >
                              <div className="mt-0.5 shrink-0">
                                {pass ? (
                                  <CheckCircle2 size={13} className="text-emerald-500" />
                                ) : (
                                  <XCircle size={13} className="text-red-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-gray-800">{name}</p>
                                {message && (
                                  <p
                                    className={`text-[11px] mt-0.5 ${
                                      pass ? "text-emerald-600" : "text-red-500"
                                    }`}
                                  >
                                    {message}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Response Details */}
                    {selectedTestCase.originalResponse && (
                      <ResponseDetails
                        response={selectedTestCase.originalResponse}
                        statusCode={selectedTestCase.expectedStatusCode}
                      />
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Clock size={40} className="text-gray-300 mb-3" />
              <p className="text-gray-600 font-medium">Select a test case</p>
              <p className="text-sm text-gray-400 mt-1">
                Click on any test case in the list to view details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleSuiteResult;
